import { CheckOutlined, CheckSquareOutlined } from '@ant-design/icons';
import {
  allSectionsOf,
  evaluateConditions,
  getInstanceCount,
  isStepEligibleForAudience,
  qualifiedKey,
  readUnitMode,
  renderInstanceLabel,
  resolveColumnLabel,
  scopeValuesToInstance,
  sectionMinInstances,
  withheldFieldIds,
  type StepAudience,
  type UnitMode,
} from '@bmo/form-core';
import { Checkbox, Empty, Typography } from 'antd';
import dayjs from 'dayjs';
import { useContext, useMemo, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  isLayoutBlockType,
  stepNavTitle,
  type FormSection,
  type RepeatableColumn,
  type WorkflowDateFormat,
  type WorkflowField,
  type WorkflowStep,
  type WorkflowTemplate,
} from '@/api/workflows';

import { DateFormatContext } from './dateFormat';
import { formatWithMask, maskedDigits } from './displayMask';
import { localizedEntryLabel } from './entryLabel';
import { RichText, type FieldValue } from './FieldRenderer';
import { DATE_INPUT_FORMAT, displayPhone } from './fieldValidation';
import { fileIcon } from './fileIcon';
import { formatFileSize } from './formatFileSize';
import { hiddenBorrowedFieldIds } from './hiddenReveals';
import { applicablePdfDocs } from './pdfPreviewActions';
import { withColon } from './reviewLabel';
import type { UploadedFilesStore } from './uploadedFiles';

const { Title, Paragraph, Text } = Typography;

// Divider hairline under every section title. Slightly heavier than the
// per-row separator in the old design so the section boundary is felt as a
// distinct band across the document.
const SECTION_HAIRLINE = '1px solid #e5e7eb';
// Muted grey used for row labels + descriptive intro copy.
const LABEL_COLOR = '#64748b';
// Bold body text — the value under each row label.
const VALUE_COLOR = '#0f172a';
// BMO blue for the markers this page draws itself (dash / checkmark list items,
// the opted-in tick-box). Same blue the runtime step marks its lists with, so a
// list reads identically on both pages.
const MARKER_COLOR = '#0179C1';
// The layout blocks an originator may opt into this recap via `showInReview`.
// Mirrors the domain's allow-list: Divider / Image carry no text to recap and
// Notice is the tinted runtime guidance the recap was deliberately cleared of,
// so the BE refuses the flag on all three. Tested here too — a hand-crafted
// template could carry it on a type the API would have rejected.
const SHOW_IN_REVIEW_TYPES: ReadonlySet<WorkflowField['type']> = new Set([
  'StaticText',
  'Paragraph',
  'BulletedList',
]);

/**
 * Read-only recap step the advisor lands on before eSignature. Renders a flat
 * scroll-through document (no accordion) with:
 *   • Intro block — page title + regulatory reminder that fields lock at sign.
 *   • One block per FormSection across every prior DynamicForm step. Each
 *     block has an uppercase title + a right-aligned "EDIT" link that
 *     navigates back to the section's owning step. Body is a 3-column CSS
 *     grid of input answers (label above value); RepeatableRow fields span
 *     the full width as their own labelled card block.
 *   • Uploaded documents + Generated forms sections at the bottom, styled
 *     the same way as the DynamicForm sections above them.
 *
 * Layout blocks authored on the runtime step (Notice / Paragraph /
 * StaticText / BulletedList / Divider / Image) are display-only context —
 * NOT the advisor's answers — so they're stripped from the recap by default,
 * and a section left with nothing else disappears from the review entirely.
 * A StaticText / Paragraph / BulletedList carrying `showInReview` is the one
 * exception: the originator opted that block in, so it recaps full-width
 * inline with the answers. It's still not an answer, so it never earns a
 * section (or step) its EDIT link.
 *
 * Values come from FormShell's live `values` map. Nothing is fetched;
 * nothing is persisted by this step.
 */
export function ApplicationReviewStep({
  template,
  step,
  values,
  language,
  pdfLanguage = language,
  uploadedFiles,
  onJumpToStep,
  audience = 'internal',
}: {
  template: WorkflowTemplate;
  step: WorkflowStep;
  values: Record<string, FieldValue>;
  /** Advisor UI language — the recap's own copy and the upload-slot names. */
  language: 'en' | 'fr';
  /** The client's chosen PDF language — names the generated forms, the same language the PDF
   *  Generation step lists and downloads them in. Defaults to `language`. */
  pdfLanguage?: 'en' | 'fr';
  /** Advisor-uploaded document store — powers the Uploaded documents recap
   *  block. Optional: absent when this step is used outside FormShell (a
   *  future standalone /review preview could omit it, in which case the
   *  block just doesn't render). */
  uploadedFiles?: UploadedFilesStore;
  /** Jump back to a specific step (clicked from the section EDIT link). */
  onJumpToStep: (stepId: string) => void;
  /** Who is reading the recap. A PUBLIC customer is recapped only what they were
   *  actually shown — an `advisorOnly` step and every advisor-sourced field are
   *  withheld here exactly as they were withheld from the form. Defaults to
   *  `'internal'`, which recaps everything. */
  audience?: StepAudience;
}) {
  const { t } = useTranslation();
  const isFr = language === 'fr';
  // The workflow's date format (QA D-01), provided by FormShell — dates recap as they were typed.
  const dateFormat = useContext(DateFormatContext);

  // Only steps that come BEFORE this review step contribute content — a
  // review placed mid-flow recaps what's been filled so far only.
  const reviewIndex = template.steps.findIndex((s) => s.id === step.id);
  // A step whose own conditions hide it is not part of this application, so it is not
  // recapped either (QA D-06: the Primary Caregiver section appeared in a flow with no
  // caregiver step). Sections and fields were already filtered; the step itself never was.
  const priorSteps = template.steps
    .slice(0, reviewIndex >= 0 ? reviewIndex : template.steps.length)
    .filter(
      (s) =>
        // Audience first, conditions second — the same order and the same reasoning as
        // FormShell's `stepVisibility`. An `advisorOnly` step was never part of the
        // customer's application, so recapping it would show them answers they never gave.
        isStepEligibleForAudience(s, audience) &&
        ((s.conditions?.length ?? 0) === 0 ||
          evaluateConditions(s.conditions, s.conditionCombinator, values)),
    );
  const dynamicSteps = priorSteps.filter((s) => s.type === 'DynamicForm');
  // Whole workflow, not just the prior steps: a "repeat once per instance of" section's
  // conditions resolve across that link wherever the other end sits.
  const allSections = useMemo(() => allSectionsOf(template), [template]);
  const additionalDocSteps = priorSteps.filter((s) => s.type === 'AdditionalDocuments');
  // Generated forms come from the PDF step; scoped to priorSteps so a mid-flow
  // review doesn't preview PDFs the advisor hasn't reached yet, and to the
  // entries whose own conditions pass so a conditioned-out PDF isn't listed.
  // Memoized because renderFilename walks the step tree per row.
  const generatedForms = useMemo(() => {
    const priorTemplate: WorkflowTemplate = { ...template, steps: priorSteps };
    return applicablePdfDocs(priorTemplate, pdfLanguage, values);
  }, [template, priorSteps, pdfLanguage, values]);
  const pdfGenerationStepId = priorSteps.find((s) => s.type === 'PdfGeneration')?.id ?? null;
  // The Welcome step's one answer — the client's document language — recapped first as its own
  // section (BMO RDSP story 329: the review opens with "Introduction", the Welcome step's title).
  // Only when the step actually asks the question.
  const welcomeStep = priorSteps.find(
    (s) => s.type === 'Welcome' && s.welcomeConfig?.showLanguageSelector !== false,
  );

  // Assemble one FlatSection per authored FormSection across every prior
  // DynamicForm step. Order preserves the workflow's authored order (steps
  // in order, sections within each step in order). Hidden sections (unmet
  // visibility conditions) are dropped entirely. Sections with zero items
  // after visibility filtering are also dropped so a section that's all
  // hidden fields doesn't produce a bare header.
  // Reveal targets whose owning option is unticked — collected across every
  // prior step so a value typed while an option was ticked doesn't linger on
  // the recap after the advisor unticked it.
  // Advisor-sourced fields join them when the reader is a public customer: both are
  // "on the page nowhere, so not in the recap either", and `collectSectionItems`
  // already skips every id in this set.
  const hiddenBorrowed = useMemo(() => {
    const all = new Set<string>();
    for (const s of dynamicSteps) {
      for (const id of hiddenBorrowedFieldIds(s, values)) all.add(id);
      for (const id of withheldFieldIds(s, audience)) all.add(id);
    }
    return all;
  }, [dynamicSteps, values, audience]);

  const dynamicSections = useMemo<FlatSection[]>(() => {
    const out: FlatSection[] = [];
    for (const s of dynamicSteps) {
      for (const section of s.sections) {
        if (
          (section.conditions?.length ?? 0) > 0 &&
          !evaluateConditions(section.conditions, section.conditionCombinator, values)
        ) {
          continue;
        }
        // A repeatable section holds one set of answers PER INSTANCE, under
        // `inst:<sectionId>:<n>:<fieldId>` keys. Recap each instance as its
        // own block so a second Accountholder's answers actually appear —
        // reading the bare field id showed the whole section blank.
        const instanceCount = section.repeatable
          ? getInstanceCount(values, section.id, sectionMinInstances(section))
          : 1;
        const baseTitle =
          (isFr ? section.titleFr : section.titleEn) || section.titleEn || section.titleFr;
        for (let i = 0; i < instanceCount; i++) {
          const items = collectSectionItems(
            section,
            values,
            isFr,
            hiddenBorrowed,
            i,
            allSections,
            dateFormat,
          );
          if (items.length === 0) continue;
          // Section is "editable" when it contains at least one input field
          // the advisor filled in (i.e. not purely a display block). Sections
          // that are all Paragraph / BulletedList / Notice still show but
          // without an EDIT link — there's nothing to edit.
          // The originator can also switch the link off for a section the advisor must not
          // revisit from here (QA D-07a: RDSP's Eligibility gate) — the answers still recap.
          // A table is an answer too: a section holding only one (RDSP PAC "Funds") had no Edit
          // link (QA observation 6).
          const hasEditableField =
            items.some(
              (i2) =>
                i2.kind === 'input' ||
                i2.kind === 'repeatable' ||
                i2.kind === 'checkbox' ||
                i2.kind === 'checkboxGroup',
            ) && section.hideEditInReview !== true;
          out.push({
            key: `${s.id}:${section.id}:${i}`,
            // Instances are numbered so "Accountholder 1" / "Accountholder 2"
            // read as distinct people rather than a repeated heading.
            title: section.repeatable ? renderInstanceLabel(section, i, language) : baseTitle,
            stepId: hasEditableField ? s.id : null,
            items,
          });
        }
      }
    }
    return out;
  }, [dynamicSteps, values, isFr, hiddenBorrowed, language, allSections, dateFormat]);

  // Opt-in (TFSA QA TF-15): a phone answer reads (416) 970-8445 here too, however it was stored —
  // the advisor's profile phone arrives as 416-970-8445.
  const formatPhones = template.formatPhoneNumbers === true;

  // Opt-in (TFSA 2026-10-06): one block per STEP, titled with the step, rather than one per
  // section. The answers are the same items the section layout shows; an acknowledgement
  // section (Eligibility) recaps as its own text, ✓ list and ticked statement.
  const byStep = template.reviewLayout === 'steps';
  const stepBlocks = useMemo<StepBlock[]>(() => {
    if (!byStep) return [];
    const out: StepBlock[] = [];
    for (const s of dynamicSteps) {
      const items: StepItem[] = [];
      let editable = false;
      for (const section of s.sections) {
        if (
          (section.conditions?.length ?? 0) > 0 &&
          !evaluateConditions(section.conditions, section.conditionCombinator, values)
        ) {
          continue;
        }
        const instanceCount = section.repeatable
          ? getInstanceCount(values, section.id, sectionMinInstances(section))
          : 1;
        for (let i = 0; i < instanceCount; i++) {
          // Every section (and repeat) after the first starts on a fresh grid row, so one
          // fund's answers never share a line with the previous one's.
          const newLine = items.length > 0;
          if (isAcknowledgementSection(section)) {
            const ack = collectAcknowledgement(section, values, isFr, i, allSections);
            if (ack.parts.length === 0) continue;
            items.push({ ...ack, newLine });
            continue;
          }
          const sectionItems = collectSectionItems(
            section,
            values,
            isFr,
            hiddenBorrowed,
            i,
            allSections,
            dateFormat,
          );
          if (sectionItems.length === 0) continue;
          // Same test as `hasEditableField` in the section layout: a section
          // contributing only opted-in display copy is nothing to edit, so it
          // must not be what earns the step its EDIT link.
          if (
            section.hideEditInReview !== true &&
            sectionItems.some(
              (it) =>
                it.kind === 'input' ||
                it.kind === 'repeatable' ||
                it.kind === 'checkbox' ||
                it.kind === 'checkboxGroup',
            )
          ) {
            editable = true;
          }
          // A repeat (Beneficiary 1, Beneficiary 2, Joint Subscriber…) is named as the form names it, or one
          // person's answers would run straight into the next one's with nothing to tell them apart.
          if (section.repeatable) {
            items.push({
              kind: 'subheading',
              text: renderInstanceLabel(section, i, language),
              newLine: true,
            });
          }
          sectionItems.forEach((it, idx) =>
            items.push({
              ...phoneShaped(it, formatPhones),
              newLine: (newLine || section.repeatable === true) && idx === 0,
            }),
          );
        }
      }
      if (items.length === 0) continue;
      out.push({
        key: s.id,
        // The rail's label, not the page heading — a block here names a step the advisor is
        // being asked to recognise from the rail they just walked. See `stepNavTitle`.
        title: stepNavTitle(s, language),
        stepId: editable ? s.id : null,
        items,
      });
    }
    return out;
  }, [
    byStep,
    dynamicSteps,
    values,
    isFr,
    hiddenBorrowed,
    allSections,
    dateFormat,
    formatPhones,
    language,
  ]);

  const hasUploaded =
    !!uploadedFiles &&
    additionalDocSteps.length > 0 &&
    (uploadedFiles.bucket.length > 0 ||
      additionalDocSteps.some((s) =>
        (s.additionalDocumentEntries ?? []).some(
          (e) => (uploadedFiles.entries[e.id] ?? []).length > 0,
        ),
      ));
  const hasGeneratedForms = generatedForms.length > 0;

  const hasAnswers = byStep ? stepBlocks.length > 0 : dynamicSections.length > 0;
  if (!hasAnswers && !hasUploaded && !hasGeneratedForms) {
    return (
      <Empty
        image={<CheckSquareOutlined style={{ fontSize: 48, color: '#cbd5e1' }} />}
        description={
          <Text type="secondary">{t('formRenderer.applicationReview.noPriorForms')}</Text>
        }
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 40 }}>
      {/* Intro — page title + regulatory reminder. Sits above every section
          so the advisor lands on it first regardless of scroll position. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Title level={3} style={{ margin: 0, color: VALUE_COLOR }}>
          {t('formRenderer.applicationReview.introTitle')}
        </Title>
        <Paragraph style={{ margin: 0, fontSize: 14, color: LABEL_COLOR, lineHeight: 1.6 }}>
          {t('formRenderer.applicationReview.introBody')}
        </Paragraph>
      </div>

      {welcomeStep && (
        <SectionBlock
          title={stepNavTitle(welcomeStep, language)}
          // Opt-in (QA RIF TC-12-01): the Introduction recap may carry no EDIT link.
          onEdit={
            welcomeStep.welcomeConfig?.hideEditInReview
              ? undefined
              : () => onJumpToStep(welcomeStep.id)
          }
          editLabel={t('formRenderer.applicationReview.edit')}
          editAria={t('formRenderer.applicationReview.editAria')}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            <Text style={{ fontSize: 13, color: LABEL_COLOR }}>
              {t('formRenderer.applicationReview.documentLanguage')}:
            </Text>
            <Text style={{ fontSize: 14, fontWeight: 600, color: VALUE_COLOR }}>
              {t(
                pdfLanguage === 'fr'
                  ? 'formRenderer.welcome.french'
                  : 'formRenderer.welcome.english',
              )}
            </Text>
          </div>
        </SectionBlock>
      )}

      {byStep &&
        stepBlocks.map((blk) => (
          <SectionBlock
            key={blk.key}
            title={blk.title}
            onEdit={blk.stepId ? () => onJumpToStep(blk.stepId!) : undefined}
            editLabel={t('formRenderer.applicationReview.edit')}
            editAria={t('formRenderer.applicationReview.editAria')}
          >
            {blk.items.map((it, idx) => renderStepItem(it, idx, isFr))}
          </SectionBlock>
        ))}

      {!byStep &&
        dynamicSections.map((sec) => (
          <SectionBlock
            key={sec.key}
            title={sec.title}
            onEdit={sec.stepId ? () => onJumpToStep(sec.stepId!) : undefined}
            editLabel={t('formRenderer.applicationReview.edit')}
            editAria={t('formRenderer.applicationReview.editAria')}
          >
            {sec.items.map((it, idx) => renderItem(phoneShaped(it, formatPhones), idx, isFr))}
          </SectionBlock>
        ))}

      {hasUploaded && uploadedFiles && (
        <SectionBlock
          title={t('formRenderer.applicationReview.uploadedDocumentsTitle')}
          onEdit={undefined}
          editLabel={t('formRenderer.applicationReview.edit')}
          editAria={t('formRenderer.applicationReview.editAria')}
        >
          <UploadedDocsGrid
            additionalDocSteps={additionalDocSteps}
            language={language}
            uploadedFiles={uploadedFiles}
            onJumpToStep={onJumpToStep}
          />
        </SectionBlock>
      )}

      {hasGeneratedForms && (
        <SectionBlock
          title={t('formRenderer.applicationReview.generatedFormsTitle')}
          onEdit={pdfGenerationStepId ? () => onJumpToStep(pdfGenerationStepId) : undefined}
          editLabel={t('formRenderer.applicationReview.edit')}
          editAria={t('formRenderer.applicationReview.editAria')}
        >
          <GeneratedFormsGrid docs={generatedForms} />
        </SectionBlock>
      )}
    </div>
  );
}

/**
 * Section block wrapper — uppercase title on the left, blue "EDIT" link on
 * the right, hairline below, then children rendered inside a 3-column CSS
 * Grid. Grid rows auto-size to content; items whose `kind === 'layout'`
 * declare `grid-column: 1 / -1` so authored blocks span the full width
 * inline with the answer rows.
 */
function SectionBlock({
  title,
  onEdit,
  editLabel,
  editAria,
  children,
}: {
  title: string;
  onEdit?: () => void;
  /** Visible text shown INSIDE the EDIT link (e.g. "Edit" / "Modifier"). */
  editLabel: string;
  /** Accessible description read by screen readers — more explicit than the
   *  visible label so the auditory context spells out what the button does. */
  editAria: string;
  children: ReactNode;
}) {
  return (
    <section>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 4,
        }}
      >
        <span
          style={{
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: 0.8,
            textTransform: 'uppercase',
            color: VALUE_COLOR,
          }}
        >
          {title}
        </span>
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            aria-label={editAria}
            style={{
              // Reset browser button chrome — this reads as a text link.
              background: 'transparent',
              border: 'none',
              padding: '4px 6px',
              cursor: 'pointer',
              color: '#0179C1',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 0.8,
              textTransform: 'uppercase',
            }}
          >
            {editLabel}
          </button>
        )}
      </div>
      <div style={{ borderBottom: SECTION_HAIRLINE, marginBottom: 20 }} />
      <div
        className="bmo-form-row"
        data-cols={3}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          columnGap: 32,
          rowGap: 24,
        }}
      >
        {children}
      </div>
    </section>
  );
}

// ─── Item model + collect + render ─────────────────────────────────────────

type FlatSection = {
  key: string;
  title: string;
  /** Owning step id for the section's EDIT link. Null when the section is
   *  purely layout blocks — nothing to edit, link is suppressed. */
  stepId: string | null;
  items: SectionItem[];
};

type SectionItem =
  | { kind: 'input'; field: WorkflowField; display: string | null }
  /** A single tick-box the originator opted into the box treatment
   *  (`reviewAsCheckbox`) — recapped as a disabled checkbox beside its label
   *  rather than a "label: ✓" answer row. It is still an answer, so it earns
   *  its section the EDIT link exactly as an `input` does. */
  | { kind: 'checkbox'; field: WorkflowField; ticked: boolean }
  /** A CheckboxGroup with at least one option ticked — recapped as the group's
   *  label above one ticked box per selection, so a multi-select carries the
   *  same symbol a single tick-box does. `labels` holds ONLY the selections;
   *  the unticked options are not recapped, exactly as they never were. An
   *  empty selection never reaches here — it stays an `input` so it keeps the
   *  italic "not provided" empty state every other unanswered field gets. */
  | { kind: 'checkboxGroup'; field: WorkflowField; labels: string[] }
  /** A StaticText / Paragraph / BulletedList the originator opted into the
   *  recap — see `collectSectionItems`. Carries no value; the field's label
   *  (and, for a list, its options) IS the copy. */
  | { kind: 'layout'; field: WorkflowField }
  | {
      kind: 'repeatable';
      field: WorkflowField;
      /** One entry per authored row — each is a `{ columnId → cell value }`
       *  map. Null when the advisor hasn't added any rows. */
      rows: Array<Record<string, unknown>>;
      /** The $/% mode each dual-unit column was answered in — the cell holds a bare number. */
      unitModes: Record<string, UnitMode>;
    };

/** One recap block per step — the opt-in `reviewLayout: 'steps'`. */
type StepBlock = {
  key: string;
  title: string;
  /** The step's EDIT target; null when none of its answers may be edited from here. */
  stepId: string | null;
  items: StepItem[];
};

/** A recap item in the per-step layout; `newLine` starts it on a fresh grid row. */
type StepItem = (SectionItem | AcknowledgementItem | { kind: 'subheading'; text: string }) & {
  newLine?: boolean;
};

/** An acknowledgement section, recapped as what the advisor read and agreed to. */
type AcknowledgementItem = {
  kind: 'acknowledgement';
  parts: Array<
    | { kind: 'text'; body: string }
    /** The authored `listStyle` travels with the items so the recap marks them
     *  the way the runtime step did — see `ListMarkers`. */
    | { kind: 'list'; items: string[]; listStyle: string | null }
    /** `asCheckbox` is the field's `reviewAsCheckbox` opt-in. */
    | { kind: 'statement'; label: string; ticked: boolean; asCheckbox: boolean }
  >;
};

/**
 * True for a section whose only answers are single tick-boxes — the RDSP-style Eligibility gate
 * ("In order to open this account…", a list, "I acknowledge"). Its answer alone ("✓") says
 * nothing, so the per-step layout recaps the text that was acknowledged. Exported for tests.
 */
export function isAcknowledgementSection(section: FormSection): boolean {
  const inputs = section.rows
    .flatMap((r) => r.columns.map((c) => c.field))
    .filter((f): f is WorkflowField => !!f && !isLayoutBlockType(f.type));
  return inputs.length > 0 && inputs.every((f) => f.type === 'Checkbox');
}

function collectAcknowledgement(
  section: FormSection,
  values: Record<string, FieldValue>,
  isFr: boolean,
  instanceIdx: number,
  allSections: readonly FormSection[],
): AcknowledgementItem {
  const condValues = scopeValuesToInstance(section, values, instanceIdx, allSections);
  const text = (en?: string | null, fr?: string | null) =>
    ((isFr ? fr : en) || en || fr || '').trim();
  const parts: AcknowledgementItem['parts'] = [];
  for (const row of section.rows) {
    for (const col of row.columns) {
      const f = col.field;
      if (!f) continue;
      if (
        (f.conditions?.length ?? 0) > 0 &&
        !evaluateConditions(f.conditions, f.conditionCombinator, condValues)
      ) {
        continue;
      }
      if (f.type === 'Paragraph' || f.type === 'StaticText') {
        const body = text(f.labelEn, f.labelFr);
        if (body) parts.push({ kind: 'text', body });
      } else if (f.type === 'BulletedList') {
        const items = f.options.map((o) => text(o.labelEn, o.labelFr)).filter(Boolean);
        if (items.length) parts.push({ kind: 'list', items, listStyle: f.listStyle ?? null });
      } else if (f.type === 'Checkbox') {
        const key = section.repeatable ? qualifiedKey(section.id, instanceIdx, f.id) : f.id;
        parts.push({
          kind: 'statement',
          label: text(f.labelEn, f.labelFr),
          ticked: values[key] === true,
          asCheckbox: f.reviewAsCheckbox === true,
        });
      }
    }
  }
  return { kind: 'acknowledgement', parts };
}

// Exported for tests — the instance/reveal handling below is easy to regress
// and fails silently (a blank recap section, not an error).
export function collectSectionItems(
  section: FormSection,
  values: Record<string, FieldValue>,
  isFr: boolean,
  hiddenBorrowed: Set<string>,
  instanceIdx: number,
  /** Every section of the workflow — see `scopeValuesToInstance`. */
  allSections?: readonly FormSection[],
  /** The workflow's date format — dates are recapped the way the advisor typed them. */
  dateFormat: WorkflowDateFormat = DATE_INPUT_FORMAT,
): SectionItem[] {
  const out: SectionItem[] = [];
  // Values of a repeatable section live under instance-qualified keys.
  const keyOf = (fieldId: string): string =>
    section.repeatable ? qualifiedKey(section.id, instanceIdx, fieldId) : fieldId;
  // Conditions name their source by bare field id; inside a repeatable
  // section that must resolve to THIS instance's answer, so the recap
  // hides exactly what the advisor's own instance had hidden.
  const condValues = scopeValuesToInstance(section, values, instanceIdx, allSections);
  for (const row of section.rows) {
    for (const col of row.columns) {
      const f = col.field;
      if (!f) continue;
      // Borrowed by a CheckboxGroup option that isn't revealing it — the
      // advisor can't see the field, so a stale value must not surface here.
      // Mirrors the same filter in buildApplicationSnapshot.
      if (hiddenBorrowed.has(f.id)) continue;
      // Field-level visibility conditions filter the item out entirely.
      if (
        (f.conditions?.length ?? 0) > 0 &&
        !evaluateConditions(f.conditions, f.conditionCombinator, condValues)
      ) {
        continue;
      }
      // Layout blocks (Notice / Paragraph / StaticText / BulletedList /
      // Divider / Image) are display-only context authored on the runtime
      // step — not answers the advisor filled in. The review recap only
      // needs to surface what the advisor entered, so we skip them by
      // default. A section that ends up empty after this filter gets dropped
      // by the caller's `items.length === 0` guard.
      // The exception is a StaticText / Paragraph / BulletedList the originator
      // explicitly opted in (`showInReview`): a disclosure, a heading that
      // gives the recapped answers their meaning, or the list of terms it
      // refers to does belong here. The flag is refused on every other layout
      // type at the BE, so the type check below is belt-and-braces against a
      // hand-crafted template.
      if (isLayoutBlockType(f.type)) {
        if (f.showInReview === true && SHOW_IN_REVIEW_TYPES.has(f.type)) {
          out.push({ kind: 'layout', field: f });
        }
        continue;
      }
      // RepeatableRow gets its own kind — the flat "join columns with —"
      // serialization looks awful in a single cell (see FUNDS section in
      // the RDSP demo). A dedicated renderer laying each row out as a
      // stacked label:value block reads properly.
      if (f.type === 'RepeatableRow') {
        const rowsRaw = values[keyOf(f.id)];
        const rows = Array.isArray(rowsRaw) ? (rowsRaw as Array<Record<string, unknown>>) : [];
        const unitModes: Record<string, UnitMode> = {};
        for (const c of f.repeatableTemplate ?? [])
          if (c.unitToggle) unitModes[c.id] = readUnitMode(f, c, (id) => values[id]);
        out.push({ kind: 'repeatable', field: f, rows, unitModes });
        continue;
      }
      // Opted in (`reviewAsCheckbox`): the tick-box recaps as the box itself,
      // so the ticked/unticked state travels as a boolean rather than through
      // formatValue's ✓ / — / "not provided" string.
      if (f.type === 'Checkbox' && f.reviewAsCheckbox === true) {
        out.push({ kind: 'checkbox', field: f, ticked: values[keyOf(f.id)] === true });
        continue;
      }
      // A CheckboxGroup's selections carry the same tick-box symbol a single
      // Checkbox does, rather than reading as a bold run of text that gives no
      // sign the advisor ticked anything. Unconditional — every multi-select is
      // a set of ticks, so there is nothing per-field to opt into.
      //
      // Nothing selected falls through deliberately: `formatValue` returns null
      // for an empty array, and an unanswered group should read as the italic
      // empty state, not as a label with no boxes under it.
      if (f.type === 'CheckboxGroup') {
        const raw = values[keyOf(f.id)];
        const labels = Array.isArray(raw) ? checkboxGroupLabels(f, raw, isFr) : [];
        if (labels.length > 0) {
          out.push({ kind: 'checkboxGroup', field: f, labels });
          continue;
        }
      }
      const display = formatValue(f, values[keyOf(f.id)], isFr, dateFormat);
      out.push({ kind: 'input', field: f, display });
    }
  }
  return out;
}

/** A phone answer in the (416) 970-8445 shape when the workflow opts in (`formatPhoneNumbers`). */
function phoneShaped<T extends SectionItem>(it: T, formatPhones: boolean): T {
  return formatPhones && it.kind === 'input' && it.field.validate === 'phone'
    ? { ...it, display: displayPhone(it.display) }
    : it;
}

function renderItem(item: SectionItem, idx: number, isFr: boolean): ReactNode {
  if (item.kind === 'input') {
    return <InputRow key={idx} field={item.field} display={item.display} isFr={isFr} />;
  }
  if (item.kind === 'checkbox') {
    return <CheckboxRow key={idx} field={item.field} ticked={item.ticked} isFr={isFr} />;
  }
  if (item.kind === 'checkboxGroup') {
    return <CheckboxGroupRow key={idx} field={item.field} labels={item.labels} isFr={isFr} />;
  }
  if (item.kind === 'layout') return <LayoutBlock key={idx} field={item.field} isFr={isFr} />;
  return (
    <RepeatableBlock
      key={idx}
      field={item.field}
      rows={item.rows}
      unitModes={item.unitModes}
      isFr={isFr}
    />
  );
}

function renderStepItem(item: StepItem, idx: number, isFr: boolean): ReactNode {
  const start: CSSProperties | undefined = item.newLine ? { gridColumnStart: 1 } : undefined;
  if (item.kind === 'acknowledgement') return <AcknowledgementBlock key={idx} item={item} />;
  if (item.kind === 'subheading') {
    return (
      <Text
        key={idx}
        style={{
          gridColumn: '1 / -1',
          fontSize: 14,
          fontWeight: 700,
          color: VALUE_COLOR,
          marginBottom: -8,
        }}
      >
        {item.text}
      </Text>
    );
  }
  if (item.kind === 'input') {
    return (
      <InputRow key={idx} field={item.field} display={item.display} isFr={isFr} style={start} />
    );
  }
  // Spans every column (acknowledgement copy is long), so `start` is a no-op here too.
  if (item.kind === 'checkbox') {
    return <CheckboxRow key={idx} field={item.field} ticked={item.ticked} isFr={isFr} />;
  }
  // Full width for the same reason — a clause list is not a one-cell answer.
  if (item.kind === 'checkboxGroup') {
    return <CheckboxGroupRow key={idx} field={item.field} labels={item.labels} isFr={isFr} />;
  }
  // Already spans every column, so `start` would be a no-op.
  if (item.kind === 'layout') return <LayoutBlock key={idx} field={item.field} isFr={isFr} />;
  return (
    <RepeatableCells
      key={idx}
      field={item.field}
      rows={item.rows}
      unitModes={item.unitModes}
      isFr={isFr}
      startStyle={start}
    />
  );
}

/** The acknowledged text — paragraph, the authored-marker list, then the statement
 *  the advisor ticked (as a disabled checkbox when the field opted in). */
function AcknowledgementBlock({ item }: { item: AcknowledgementItem }) {
  const { t } = useTranslation();
  return (
    <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 12 }}>
      {item.parts.map((p, i) => {
        if (p.kind === 'text') {
          return <RichText key={i} body={p.body} style={{ fontSize: 14, color: VALUE_COLOR }} />;
        }
        if (p.kind === 'list') {
          return <MarkedList key={i} items={p.items} listStyle={p.listStyle} />;
        }
        if (p.asCheckbox) {
          return <CheckboxLine key={i} label={p.label} ticked={p.ticked} />;
        }
        return (
          <Text
            key={i}
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: p.ticked ? VALUE_COLOR : '#6b7280',
              fontStyle: p.ticked ? undefined : 'italic',
            }}
          >
            {p.ticked
              ? p.label.replace(/\*\*/g, '').trim()
              : `${p.label.replace(/\*\*/g, '').trim()}: ${t('formRenderer.applicationReview.noValue')}`}
          </Text>
        );
      })}
    </div>
  );
}

/**
 * One list, marked the way the originator authored it — the same four styles
 * the runtime step draws (see FieldRenderer's BulletedList): `decimal` as a
 * real `<ol>`, `dash` and `checkmark` with a marker we draw ourselves (CSS has
 * no list-style-type for either), anything else as the default disc.
 *
 * Shared by the opted-in BulletedList block and the acknowledgement recap so
 * the two cannot drift: a list is marked by what the originator picked,
 * wherever on the recap it lands.
 *
 * Items go through RichText `variant="inline"` so authored markup survives
 * without a block wrapper fighting the `<li>`'s own layout.
 */
function MarkedList({ items, listStyle }: { items: string[]; listStyle: string | null }) {
  const base: CSSProperties = { margin: 0, display: 'flex', flexDirection: 'column', gap: 10 };
  const body = (text: string) => (
    <RichText
      body={text}
      variant="inline"
      style={{ fontSize: 14, color: VALUE_COLOR, lineHeight: 1.55 }}
    />
  );
  // A native marker (disc / decimal) hangs in the padding the list reserves for
  // it; a marker we draw ourselves sits INSIDE the row and must not be pushed
  // off the grid column's left edge, so those two get no indent.
  if (listStyle === 'decimal' || listStyle === 'dash' || listStyle === 'checkmark') {
    if (listStyle === 'decimal') {
      return (
        <ol style={{ ...base, listStyle: 'decimal', padding: 0, paddingLeft: 20 }}>
          {items.map((text, i) => (
            <li key={i} style={{ display: 'list-item' }}>
              {body(text)}
            </li>
          ))}
        </ol>
      );
    }
    const isCheck = listStyle === 'checkmark';
    return (
      <ul style={{ ...base, listStyle: 'none', padding: 0 }}>
        {items.map((text, i) => (
          <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            {isCheck ? (
              <CheckOutlined
                aria-hidden
                style={{ color: MARKER_COLOR, fontSize: 14, marginTop: 4, flexShrink: 0 }}
              />
            ) : (
              <span
                aria-hidden
                style={{
                  color: MARKER_COLOR,
                  fontWeight: 600,
                  lineHeight: 1.55,
                  flexShrink: 0,
                }}
              >
                —
              </span>
            )}
            {body(text)}
          </li>
        ))}
      </ul>
    );
  }
  return (
    <ul style={{ ...base, listStyle: 'disc', padding: 0, paddingLeft: 20 }}>
      {items.map((text, i) => (
        <li key={i} style={{ display: 'list-item' }}>
          {body(text)}
        </li>
      ))}
    </ul>
  );
}

/**
 * A tick-box recapped as the box itself (`reviewAsCheckbox`) — a disabled
 * checkbox carrying its real state, with the authored label beside it. An
 * eligibility gate ("I acknowledge …") is an agreement rather than a value, and
 * an empty box says "this was not accepted" far more directly than an italic
 * "Not provided" row does.
 *
 * The checkbox is disabled, never read-only-by-styling: the recap must not be a
 * place where an acknowledgement can be given. `.bmo-review-checkbox` restores
 * the contrast AntD greys out of a disabled control (see global.css) — the box
 * IS the record here, so it has to stay legible.
 */
function CheckboxLine({
  label,
  ticked,
  style,
}: {
  /** Authored label, markdown and all — rendered through RichText. */
  label: string;
  ticked: boolean;
  style?: CSSProperties;
}) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', minWidth: 0, ...style }}>
      <Checkbox
        className="bmo-review-checkbox"
        checked={ticked}
        disabled
        aria-label={label.replace(/\*\*/g, '').trim()}
        style={{ marginTop: 2, flexShrink: 0 }}
      />
      <RichText
        body={label}
        variant="inline"
        style={{ fontSize: 14, fontWeight: 600, color: VALUE_COLOR, lineHeight: 1.55 }}
      />
    </div>
  );
}

/** The opted-in tick-box in a recap grid — full width, since the copy it carries
 *  is a clause, not a one-cell answer. */
function CheckboxRow({
  field,
  ticked,
  isFr,
}: {
  field: WorkflowField;
  ticked: boolean;
  isFr: boolean;
}) {
  const label = (isFr ? field.labelFr : field.labelEn) || field.labelEn || field.labelFr || '';
  return <CheckboxLine label={label.trim()} ticked={ticked} style={{ gridColumn: '1 / -1' }} />;
}

/**
 * A CheckboxGroup's selections, each drawn with the same disabled tick-box a
 * single `reviewAsCheckbox` Checkbox gets. The joined bold text this replaced
 * carried no sign that the advisor had ticked anything — it read as a typed
 * answer, when what it records is a set of agreements (Carlyle's accredited-
 * investor clauses, a list of authorizations).
 *
 * Unlike the single tick-box, the field's own label is kept as its own row: for
 * a group the label asks the QUESTION ("Accredited investor categories") and
 * the options are the clauses, so dropping it would leave the ticks unanchored.
 * Blank-labelled groups skip the row rather than reserve an empty line.
 *
 * Every box is ticked because only the selections are listed — an unticked
 * option is recapped by its absence, the same way it always has been.
 */
function CheckboxGroupRow({
  field,
  labels,
  isFr,
}: {
  field: WorkflowField;
  /** The selected options' labels — see `checkboxGroupLabels`. Never empty. */
  labels: string[];
  isFr: boolean;
}) {
  const rawLabel = (isFr ? field.labelFr : field.labelEn) || field.labelEn || field.labelFr || '';
  const label = rawLabel.replace(/\*\*/g, '').trim();
  return (
    <div
      style={{
        gridColumn: '1 / -1',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      {label && <Text style={{ fontSize: 13, color: LABEL_COLOR }}>{withColon(label)}</Text>}
      {labels.map((l, i) => (
        <CheckboxLine key={i} label={l} ticked />
      ))}
    </div>
  );
}

/**
 * A table's answers laid straight into the step's grid — each row's cells as ordinary
 * label/value pairs, every row starting on a fresh line (the per-step layout's Fund name /
 * Fund number / $ value). No rows reads as the table's label with "—".
 */
function RepeatableCells({
  field,
  rows,
  unitModes,
  isFr,
  startStyle,
}: {
  field: WorkflowField;
  rows: Array<Record<string, unknown>>;
  unitModes: Record<string, UnitMode>;
  isFr: boolean;
  startStyle?: CSSProperties;
}) {
  const dateFormat = useContext(DateFormatContext);
  const cards = repeatableCards(field, rows, unitModes, isFr, dateFormat);
  if (cards.length === 0) {
    return <InputRow field={field} display={null} isFr={isFr} style={startStyle} />;
  }
  return (
    <>
      {cards.flatMap((cells, r) =>
        cells.map((c, i) => (
          <LabelValue
            key={`${r}:${i}`}
            label={c.label}
            value={c.value}
            style={i === 0 && (r > 0 || startStyle) ? { gridColumnStart: 1 } : undefined}
          />
        )),
      )}
    </>
  );
}

function LabelValue({
  label,
  value,
  style,
}: {
  label: string;
  value: string;
  style?: CSSProperties;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, ...style }}>
      <Text style={{ fontSize: 13, color: LABEL_COLOR }}>{withColon(label)}</Text>
      <Text style={{ fontSize: 14, fontWeight: 600, color: VALUE_COLOR, wordBreak: 'break-word' }}>
        {value}
      </Text>
    </div>
  );
}

/**
 * Label-above-value input row — occupies a single 3-col grid cell. Label
 * is grey light 13 px; value is bold 14 px dark. Empty required inputs
 * render "—" in italic muted grey so the reviewer notices what
 * wasn't answered.
 */
function InputRow({
  field,
  display,
  isFr,
  style,
}: {
  field: WorkflowField;
  display: string | null;
  isFr: boolean;
  style?: CSSProperties;
}) {
  const { t } = useTranslation();
  const rawLabel = (isFr ? field.labelFr : field.labelEn) || field.labelEn || field.labelFr || '';
  // Labels are authored with the ** markdown bold pair. Strip them for the
  // review recap (bold is the row's default style anyway).
  const label = rawLabel.replace(/\*\*/g, '').trim();
  const isEmpty = display === null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, ...style }}>
      <Text style={{ fontSize: 13, color: LABEL_COLOR }}>{withColon(label)}</Text>
      <Text
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: isEmpty ? '#6b7280' : VALUE_COLOR,
          fontStyle: isEmpty ? 'italic' : undefined,
          whiteSpace: 'pre-line',
          wordBreak: 'break-word',
        }}
      >
        {display ?? t('formRenderer.applicationReview.noValue')}
      </Text>
    </div>
  );
}

/**
 * Full-width opted-in layout block — spans all 3 columns of the grid so the
 * authored copy reads inline with the answer rows. Only StaticText, Paragraph
 * and BulletedList reach here (`collectSectionItems` filters, the BE refuses
 * the `showInReview` flag elsewhere).
 *
 * Rendered through RichText so authored `**bold**`, `[label](url)` and line
 * breaks survive — plain text left the raw markdown syntax visible. StaticText
 * keeps the font size / weight / colour the originator picked, since it's
 * usually acting as a heading and losing that would flatten it into the
 * surrounding body copy; Paragraph takes the muted recap style, matching the
 * label colour of the answer rows around it. Authored margins are deliberately
 * NOT carried over — the recap grid owns its own vertical rhythm.
 *
 * Empty content renders nothing, so the grid never gets a phantom row.
 */
function LayoutBlock({ field, isFr }: { field: WorkflowField; isFr: boolean }) {
  const text = (isFr ? field.labelFr : field.labelEn) || field.labelEn || field.labelFr || '';
  const body = text.trim();
  const style: CSSProperties = { gridColumn: '1 / -1', minWidth: 0 };
  // BulletedList's label is an optional intro line and its OPTIONS are the
  // items, so it's the one block that can be worth recapping with an empty
  // label. Handled before the shared `!body` bail for that reason.
  if (field.type === 'BulletedList') {
    return <BulletList field={field} isFr={isFr} intro={body} style={style} />;
  }
  if (!body) return null;
  if (field.type === 'StaticText') {
    return (
      <RichText
        body={body}
        variant="block"
        style={{
          ...style,
          fontSize: field.fontSize ?? 14,
          fontWeight: field.fontWeight === 'bold' ? 700 : 500,
          color: field.color || VALUE_COLOR,
        }}
      />
    );
  }
  return (
    <RichText
      body={body}
      variant="block"
      style={{ ...style, fontSize: 14, color: LABEL_COLOR, lineHeight: 1.6 }}
    />
  );
}

/**
 * An opted-in BulletedList — the field's label as an optional intro line, then
 * its `options` as the items, marked with the style the originator authored
 * (`MarkedList`).
 *
 * A list with no intro AND no items renders nothing, so the grid never gets a
 * phantom row.
 */
function BulletList({
  field,
  isFr,
  intro,
  style,
}: {
  field: WorkflowField;
  isFr: boolean;
  /** The field's trimmed label, or '' when it was authored blank. */
  intro: string;
  style: CSSProperties;
}) {
  const items = (field.options ?? [])
    .map((o) => ((isFr ? o.labelFr : o.labelEn) || o.labelEn || o.labelFr || '').trim())
    .filter(Boolean);
  if (!intro && items.length === 0) return null;
  return (
    <div style={{ ...style, display: 'flex', flexDirection: 'column', gap: 12 }}>
      {intro && (
        <RichText
          body={intro}
          variant="block"
          style={{ fontSize: 14, color: VALUE_COLOR, lineHeight: 1.55 }}
        />
      )}
      {items.length > 0 && <MarkedList items={items} listStyle={field.listStyle ?? null} />}
    </div>
  );
}

/**
 * Full-width RepeatableRow recap — spans all 3 columns of the parent grid.
 * Renders the field's label as a small caption, then one card per authored
 * row with its cells laid out in a 3-column mini-grid (label above value,
 * same treatment as the outer InputRow). Multi-row RepeatableRows stack
 * their cards with a subtle hairline separator between them. Zero-row /
 * unset RepeatableRows fall back to a single "—" line so the
 * reviewer knows something was skipped rather than the block silently
 * vanishing.
 */
function RepeatableBlock({
  field,
  rows,
  unitModes,
  isFr,
}: {
  field: WorkflowField;
  rows: Array<Record<string, unknown>>;
  unitModes: Record<string, UnitMode>;
  isFr: boolean;
}) {
  const { t } = useTranslation();
  const dateFormat = useContext(DateFormatContext);
  const style: CSSProperties = { gridColumn: '1 / -1', minWidth: 0 };
  const rawLabel = (isFr ? field.labelFr : field.labelEn) || field.labelEn || '';
  const label = rawLabel.replace(/\*\*/g, '').trim();
  const cards = repeatableCards(field, rows, unitModes, isFr, dateFormat);
  return (
    <div style={{ ...style, display: 'flex', flexDirection: 'column', gap: 12 }}>
      {label && <Text style={{ fontSize: 13, color: LABEL_COLOR }}>{withColon(label)}</Text>}
      {cards.length === 0 ? (
        <Text
          style={{
            fontSize: 14,
            color: '#6b7280',
            fontStyle: 'italic',
          }}
        >
          {t('formRenderer.applicationReview.noValue')}
        </Text>
      ) : (
        cards.map((cells, idx) => (
          <div
            key={idx}
            className="bmo-form-row"
            data-cols={3}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              columnGap: 32,
              rowGap: 16,
              // Hairline between cards; the first card has no top border so it
              // sits flush with the caption.
              borderTop: idx > 0 ? SECTION_HAIRLINE : 'none',
              paddingTop: idx > 0 ? 16 : 0,
            }}
          >
            {cells.map((c, i) => (
              <LabelValue key={i} label={c.label} value={c.value} />
            ))}
          </div>
        ))
      )}
    </div>
  );
}

/** Each filled row of a table as its non-empty `{ label, value }` cells, in column order. */
function repeatableCards(
  field: WorkflowField,
  rows: Array<Record<string, unknown>>,
  unitModes: Record<string, UnitMode>,
  isFr: boolean,
  dateFormat: WorkflowDateFormat,
): Array<Array<{ label: string; value: string }>> {
  const template: RepeatableColumn[] = field.repeatableTemplate ?? [];
  // Walk each row's cells against the authored column template so the
  // display order and labels match what the originator wired — the `values`
  // map keys on column.id, not label. Empty cells are hidden per row so a
  // half-filled row doesn't waste a grid slot on "—".
  return rows
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const cells = template
        .map((col) => {
          const raw = row[col.id];
          if (raw === null || raw === undefined || raw === '') return null;
          const colLabel = resolveColumnLabel(col, isFr ? 'fr' : 'en', unitModes[col.id]);
          return {
            label: colLabel.replace(/\*\*/g, '').trim(),
            // A dual-unit amount is a bare number; its unit is the mode it was answered in,
            // so a percentage share reads "60%" rather than "60" (QA TFSA-D23).
            value: formatCell(
              unitModes[col.id]
                ? ({
                    ...col,
                    numberFormat: unitModes[col.id] === 'percent' ? 'percentage' : 'currency',
                  } as RepeatableColumn)
                : col,
              raw,
              isFr,
              dateFormat,
            ),
          };
        })
        .filter((c): c is { label: string; value: string } => c !== null);
      return cells.length > 0 ? cells : null;
    })
    .filter((c): c is Array<{ label: string; value: string }> => c !== null);
}

// ─── Uploaded documents + Generated forms grids ────────────────────────────
//
// Both share the same 3-col grid the DynamicForm sections use — one card per
// file / generated form.

function UploadedDocsGrid({
  additionalDocSteps,
  language,
  uploadedFiles,
  onJumpToStep,
}: {
  additionalDocSteps: WorkflowStep[];
  language: 'en' | 'fr';
  uploadedFiles: UploadedFilesStore;
  onJumpToStep: (stepId: string) => void;
}) {
  const { t } = useTranslation();
  type Row = {
    key: string;
    label: string;
    fileName: string;
    size: number;
    description: string | null;
    stepId: string;
  };
  const rows: Row[] = [];
  for (const s of additionalDocSteps) {
    for (const entry of s.additionalDocumentEntries ?? []) {
      const files = uploadedFiles.entries[entry.id] ?? [];
      for (const f of files) {
        rows.push({
          key: `${entry.id}:${f.uid}`,
          label: localizedEntryLabel(entry, language),
          fileName: f.file.name,
          size: f.file.size,
          description: null,
          stepId: s.id,
        });
      }
    }
  }
  const lastDocStepId = additionalDocSteps[additionalDocSteps.length - 1]?.id;
  for (const f of uploadedFiles.bucket) {
    rows.push({
      key: `bucket:${f.uid}`,
      label: t('formRenderer.applicationReview.optionalDocumentLabel'),
      fileName: f.file.name,
      size: f.file.size,
      description: f.description?.trim() ? f.description.trim() : null,
      stepId: lastDocStepId ?? '',
    });
  }
  if (rows.length === 0) {
    return (
      <Text type="secondary" style={{ fontSize: 13, gridColumn: '1 / -1' }}>
        {t('formRenderer.applicationReview.noDocuments')}
      </Text>
    );
  }
  return (
    <>
      {rows.map((r) => (
        <FileCard
          key={r.key}
          icon={fileIcon(r.fileName)}
          label={r.label}
          primary={r.fileName}
          secondary={`${formatFileSize(r.size)}${r.description ? ` · ${r.description}` : ''}`}
          onClick={r.stepId ? () => onJumpToStep(r.stepId) : undefined}
        />
      ))}
    </>
  );
}

function GeneratedFormsGrid({ docs }: { docs: ReturnType<typeof applicablePdfDocs> }) {
  return (
    <>
      {docs.map((d) => (
        <FileCard
          key={d.entryId}
          icon={fileIcon(d.fileName)}
          label={d.label}
          primary={d.fileName}
          secondary=""
        />
      ))}
    </>
  );
}

/** One card in the Uploaded / Generated grid — icon + label above a filename
 *  + secondary meta. Whole card is clickable when a jump target exists so
 *  keyboard + touch users can reach the same edit affordance the section
 *  header's EDIT link provides. */
function FileCard({
  icon,
  label,
  primary,
  secondary,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  primary: string;
  secondary: string;
  onClick?: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        minWidth: 0,
        cursor: onClick ? 'pointer' : 'default',
      }}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        {icon}
        <Text style={{ fontSize: 13, color: LABEL_COLOR }}>{label}</Text>
      </div>
      <Text
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: VALUE_COLOR,
          wordBreak: 'break-word',
        }}
      >
        {primary}
      </Text>
      {secondary && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {secondary}
        </Text>
      )}
    </div>
  );
}

/**
 * One table cell, for display. A column carries the same `type` + `options` as a field, so it
 * goes through the same {@link formatValue}: a date reads in the workflow's format and a choice
 * reads as its label, rather than the stored ISO date and option key the PAC table used to show
 * (QA D-20). Exported for tests.
 */
export function formatCell(
  col: RepeatableColumn,
  raw: unknown,
  isFr: boolean,
  dateFormat: WorkflowDateFormat = DATE_INPUT_FORMAT,
): string {
  return (
    formatValue(col as unknown as WorkflowField, raw as FieldValue, isFr, dateFormat) ?? String(raw)
  );
}

/**
 * The selected options of a CheckboxGroup, resolved to their labels in the
 * advisor's language and in the order the advisor's answer holds them.
 *
 * Shared by the ticked-box recap and by `formatValue`'s flat-string fallback so
 * the two renderings cannot resolve a selection differently. A value that
 * matches no authored option is dropped — a stale key from a since-edited
 * option list must not surface as a raw code.
 */
function checkboxGroupLabels(field: WorkflowField, value: unknown[], isFr: boolean): string[] {
  return (
    value
      .map((v) => field.options.find((o) => o.value === v))
      .filter((o): o is NonNullable<typeof o> => !!o)
      .map((o) => (isFr ? o.labelFr : o.labelEn) || o.labelEn || o.labelFr)
      // Long regulatory clause labels frequently end with their own trailing
      // comma (e.g. "(a) … financial institution,"). Strip it so a line doesn't
      // close on dangling punctuation.
      .map((label) => label.replace(/[,;.\s]+$/, ''))
  );
}

// ─── formatValue — unchanged from earlier revs; workhorse for every input row ───

/**
 * Stringify a field's stored value for read-only display. Returns null when
 * the field is genuinely empty (so the row can render "—" in
 * italics + grey). Resolves SingleSelect / Radio / Checkbox option values
 * back to their display labels in the advisor's language. Number values
 * pick up `$` / `%` prefixes per the field's numberFormat; Date values
 * format in the workflow's date format so the recap matches what the advisor typed.
 */
function formatValue(
  field: WorkflowField,
  value: FieldValue,
  isFr: boolean,
  dateFormat: WorkflowDateFormat,
): string | null {
  if (value === null || value === undefined) return null;

  // Date fields are stored as ISO strings (`YYYY-MM-DD`). Localise to the
  // same format the picker uses (the workflow's) so the recap doesn't expose
  // the wire shape.
  if (field.type === 'Date' && typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length === 0) return null;
    const d = dayjs(trimmed);
    return d.isValid() ? d.format(dateFormat) : trimmed;
  }

  // Number fields — sample-values fixtures sometimes ship the value as a
  // string (`'125000'`); FormShell's onChange may also pass strings via
  // typed input. Parse-and-format here so a stringified number still gets
  // currency / percentage decoration.
  if (field.type === 'Number') {
    const n =
      typeof value === 'number'
        ? value
        : typeof value === 'string' && value.trim().length > 0
          ? Number(value)
          : NaN;
    if (!Number.isFinite(n)) return null;
    const formatted =
      field.numberFormat === 'currency'
        ? n.toLocaleString(isFr ? 'fr-CA' : 'en-CA', {
            style: 'currency',
            currency: 'CAD',
            // CAD options always include cents — strip them when the value is
            // a clean integer so $125,000 doesn't read as $125,000.00.
            minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
          })
        : field.numberFormat === 'percentage'
          ? `${n.toLocaleString(isFr ? 'fr-CA' : 'en-CA')}%`
          : n.toLocaleString(isFr ? 'fr-CA' : 'en-CA');
    return formatted;
  }

  // Option-label lookup wins over the raw-string / boolean branches: a
  // SingleSelect / RadioGroup that happens to persist a stringified or
  // boolean value (e.g. 'true' / true for a Yes/No-shaped picker) still
  // resolves to the originator-authored option label in the advisor's
  // language. Falls through to the type-specific branches below when the
  // value doesn't match any option.
  if (field.type === 'SingleSelect' || field.type === 'RadioGroup') {
    const opt = field.options.find((o) => String(o.value) === String(value));
    if (opt) return (isFr ? opt.labelFr : opt.labelEn) || opt.labelEn || opt.labelFr;
  }
  if (typeof value === 'string') {
    if (value.trim().length === 0) return null;
    // Stored as digits, recapped the way the advisor saw it typed (QA D-21: 123-456789).
    if (field.type === 'ShortText' && field.displayMask) {
      return formatWithMask(field.displayMask, maskedDigits(field.displayMask, value));
    }
    return value;
  }
  if (typeof value === 'number') return Number.isFinite(value) ? value.toString() : null;
  if (typeof value === 'boolean') {
    // A required Toggle/Checkbox that's still `false` is the acknowledgment
    // pattern — surface as "not provided" (null → italic empty-state) so
    // the advisor isn't misled into thinking they ticked it. Optional
    // boolean stays as a visible ✓ / — pair.
    if (field.required && value === false) return null;
    return value ? '✓' : '—';
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    if (field.type === 'CheckboxGroup') {
      // One selection per line. A `; ` join read fine for two or three short
      // options but collapsed Carlyle's a..w accredited-investor clauses into
      // an unreadable wall of bold text once the advisor ticked them all. The
      // value Text renders with `white-space: pre-line`, so the newlines break.
      //
      // Only a CheckboxGroup authored as a repeatable COLUMN still reaches this
      // flat string — a field-level one recaps as ticked boxes instead (see
      // `collectSectionItems`), off the same labels.
      const labels = checkboxGroupLabels(field, value, isFr);
      // Every stored value pointed at an option that no longer exists, so there
      // is nothing to show: that is "not provided", not a blank bold line.
      return labels.length > 0 ? labels.join('\n') : null;
    }
    // RepeatableRow value — array of objects keyed by column id. Resolve
    // each row to a "col-label: value" pair, join rows with newlines so
    // multi-row entries are readable in the recap.
    if (field.type === 'RepeatableRow' && field.repeatableTemplate) {
      const tmpl = field.repeatableTemplate;
      const rows = value as Array<Record<string, unknown>>;
      const formattedRows = rows
        .map((row) => {
          if (row === null || typeof row !== 'object') return '';
          const cells = tmpl
            .map((col) => {
              const cell = row[col.id];
              if (cell === null || cell === undefined || cell === '') return null;
              // No answer map reaches this flat fallback serializer, so a `{type}`
              // token resolves to the column's authored default rather than the
              // advisor's live choice. Stale beats printing the raw token.
              const colLabel = resolveColumnLabel(col, isFr ? 'fr' : 'en');
              return `${colLabel}: ${formatCell(col, cell, isFr, dateFormat)}`;
            })
            .filter((s): s is string => s !== null);
          return cells.join(' — ');
        })
        .filter((s) => s.length > 0);
      if (formattedRows.length === 0) return null;
      return formattedRows.join('\n');
    }
    return value.map((v) => String(v)).join(', ');
  }
  return null;
}
